<template>
  <vx-card class="mt-base">
    <heading size="h2" style-size="h4" class="mb-5">{{ activityTitle }}</heading>
    <custom-video
      aria-label="Watch becoming becoming registered video"
      elem-id="video-btn-0"
      :video-image="require('@/assets/images/experiences/psychology/activity_6_how_to_splash.jpg')"
      :video-url="popupVideo"
    />

    <p class="mb-5">
      <a
        href="https://www.psychologytoday.com/au/contributors/susan-krauss-whitbourne-phd"
        target="_blank"
        >Susan Krauss Whitbourne</a
      >
      provided a quiz and states that it tests your knowledge of the main features of a number of
      common diagnoses.
    </p>
    <p class="mb-5">
      <strong><u>Note:</u></strong> Don’t diagnose yourself from these symptoms. Just use the quiz
      to see how much you know about this very important field within psychology.
    </p>
    <p class="mb-5">
      The Diagnostic and Statistical Manual of Mental Disorders (DSM) is another standard
      classification of mental disorders used by mental health professionals.
    </p>
    <p class="mb-5">
      The
      <a
        href="https://www.psychologytoday.com/au/blog/fulfillment-any-age/201305/what-the-dsm-5-changes-mean-you"
        target="_blank"
        >DSM-5</a
      >
      lists in detail the many symptoms required for diagnosing psychological disorders.
    </p>
    <p class="mb-5">
      See if you can match the disorder to the symptoms by going to each link and reading up on each
      disorder.
    </p>

    <draggable class="list-group" :list="list1" group="people">
      <div class="list-group-item" v-for="element in list1" :key="element.name">
        <span v-html="element.name"></span>
      </div>
    </draggable>

    <div
      class="vx-row flex items-center justify-center"
      v-for="(question, index) in questions"
      :key="index"
      :class="{ wrong: !correct_answers['question' + index] }"
    >
      <div class="vx-col xl:w-1/2 lg:w-2/3 w-full my-5">{{ question.question }}</div>
      <div class="vx-col xl:w-1/2 lg:w-2/3 w-full my-5">
        <draggable
          class="list-group draggable-area"
          :list="answers['question' + index]"
          group="people"
          @change="removeOldSelection(index)"
        >
          <div
            class="list-group-item"
            v-for="element in answers['question' + index]"
            :key="element.name"
          >
            <span v-html="element.name"></span>
          </div>
        </draggable>
        <match-select
          :pool="list1"
          :answer="answers['question' + index]"
          :label="question.question"
          :editable="isEditable"
        />
      </div>
    </div>
    <div class="overflow-hidden mb-5" v-if="isEditable">
      <button class="btn btn-secondary btn-size-medium mr-5" @click="checkAnswers">
        Check Answers
      </button>
      <button class="btn btn-secondary danger btn-size-medium" @click="resetAnswers">Reset</button>
    </div>

    <save-buttons :is-editable="isEditable" @save="save" />
  </vx-card>
</template>

<script>
import draggable from 'vuedraggable';
import MatchSelect from '@/components/UI/MatchSelect';
import computed from '@/views/WorkExperiences/components/activity_js/computed';
import inject from '@/views/WorkExperiences/components/activity_js/inject';
import CustomVideo from '@/components/CustomVideo';

export default {
  components: { MatchSelect, CustomVideo, draggable },
  data() {
    return {
      login_type: localStorage.getItem('login_type'),
      popupVideo:
        $vjeConfig.VUE_APP_VJE_PUBLIC_OBJECT_STORAGE_URL +
        '/content/psychology/psych_activity_6_how_to.mp4',
      questions: {
        1: {
          question: 'Restlessness and general discomfort, worried much of the time with no reason',
        },
        2: {
          question:
            'Engaging in repetitive behaviour associated with troublesome and persistent thoughts that are unwanted and intrusive',
        },
        3: {
          question:
            'Having periods of elated mood in which the individual is highly talkative, excitable, and distractible',
        },
        4: {
          question:
            'Suffering from false beliefs that others are monitoring your thoughts and attempting to do you extreme bodily harm',
        },
        5: {
          question:
            'Having a variety of physical symptoms for which a medical cause cannot be found',
        },
        6: {
          question:
            'Being tormented by flashbacks and frightening images after being involved in a near-fatal car accident three weeks ago',
        },
        7: {
          question:
            'Staying home due to fear of having a panic attack while in a crowded situation from which escape isn’t possible',
        },
        8: {
          question:
            'Being convinced that your slight cough means that you have pneumonia or lung cancer',
        },
        9: {
          question:
            'Refusing to eat in front of other people out of fear that you will spill your food or in other ways look ridiculous',
        },
        10: {
          question:
            'Engaging in frequent illegal acts such as conning other people out of their hard-earned savings and engaging in petty theft without feeling a sense of remorse or guilt',
        },
      },
      answers: {
        question1: [],
        question2: [],
        question3: [],
        question4: [],
        question5: [],
        question6: [],
        question7: [],
        question8: [],
        question9: [],
        question10: [],
      },
      correct_answers: {
        question1: true,
        question2: true,
        question3: true,
        question4: true,
        question5: true,
        question6: true,
        question7: true,
        question8: true,
        question9: true,
        question10: true,
      },
      list1: [
        {
          name: `<a href="https://www.psychologytoday.com/au/conditions/generalized-anxiety-disorder" target="_blank">General anxiety disorder</a>`,
          id: 1,
        },
        {
          name: `<a href="https://www.psychologytoday.com/au/conditions/obsessive-compulsive-disorder" target="_blank">Obsessive-compulsive disorder</a>`,
          id: 2,
        },
        {
          name: `<a href="https://www.psychologytoday.com/au/basics/bipolar-disorder" target="_blank">Bipolar disorder</a>`,
          id: 3,
        },
        {
          name: `<a href="https://www.psychologytoday.com/au/conditions/schizophrenia" target="_blank">Schizophrenia</a>`,
          id: 4,
        },
        {
          name: `<a href="https://www.psychologytoday.com/au/conditions/somatic-symptom-disorder" target="_blank">Somatic symptom disorder</a>`,
          id: 5,
        },
        {
          name: `<a href="https://www.psychologytoday.com/au/conditions/acute-stress-disorder" target="_blank">Acute stress disorder</a> (NOT <a href="https://www.psychologytoday.com/au/basics/post-traumatic-stress-disorder" target="_blank">PTSD</a>, because it’s less than a month)`,
          id: 6,
        },
        {
          name: `<a href="https://www.psychologytoday.com/au/conditions/agoraphobia" target="_blank">Agoraphobia</a>`,
          id: 7,
        },
        {
          name: `<a href="https://www.psychologytoday.com/au/conditions/illness-anxiety-disorder" target="_blank">Illness anxiety disorder</a>`,
          id: 8,
        },
        {
          name: `<a href="https://www.psychologytoday.com/au/conditions/social-anxiety-disorder-social-phobia" target="_blank">Social anxiety disorder</a>`,
          id: 9,
        },
        {
          name: `<a href="https://www.psychologytoday.com/au/conditions/antisocial-personality-disorder" target="_blank">Antisocial personality disorder</a>`,
          id: 10,
        },
      ],
    };
  },
  computed: computed,
  inject: inject,
  methods: {
    save() {
      this.data = this.answers;
      this.saveActivity(this.$route.params.slug, this.data, -1);
    },
    removeOldSelection(index) {
      if (this.answers['question' + index].length > 1) {
        let lastElem =
          this.answers['question' + index][this.answers['question' + index].length - 1];
        for (let i = 0; i < this.answers['question' + index].length - 1; i++) {
          this.list1.push(this.answers['question' + index][i]);
        }
        this.answers['question' + index] = [];
        this.answers['question' + index].push(lastElem);
      }
    },
    checkAnswers() {
      for (let i = 1; i <= 10; i++) {
        if (this.answers['question' + i].length) {
          if (this.answers['question' + i][0].id != i) {
            this.correct_answers['question' + i] = false;
          } else {
            this.correct_answers['question' + i] = true;
          }
        }
      }
    },
    resetAnswers() {
      for (let index = 1; index <= 10; index++) {
        if (this.answers['question' + index].length > 0) {
          let lastElem = this.answers['question' + index][0];
          this.answers['question' + index] = [];
          this.correct_answers['question' + index] = true;
          this.list1.push(lastElem);
        }
      }
      this.list1 = this.list1.sort(() => 0.5 - Math.random());
    },
  },
  mounted() {
    if (typeof this.data.question1 !== 'undefined') {
      this.answers = this.data;

      for (let i = 1; i <= 10; i++) {
        if (this.answers['question' + i].length > 0) {
          let id = this.answers['question' + i][0].id;
          this.list1 = this.list1.filter(function (item) {
            return item.id !== id;
          });
        }
      }
    }
    //Shuffle the answers array
    this.list1 = this.list1.sort(() => 0.5 - Math.random());
    this.checkAnswers();
  },
};
</script>

<style scoped>
.wrong {
  background: rgba(202, 20, 20, 0.24);
  border-bottom: 1px solid #f8f8f8;
  padding-top: 2px;
  margin: 0;
}

.correct {
  background: rgba(75, 202, 20, 0.24);
  border-bottom: 1px solid #f8f8f8;
}

.list-group {
  display: -ms-flexbox;
  display: -webkit-box;
  display: flex;
  -ms-flex-direction: column;
  -webkit-box-orient: vertical;
  -webkit-box-direction: normal;
  flex-direction: column;
  padding-left: 0;
  margin-bottom: 0;
}

.list-group-item {
  position: relative;
  display: block;
  padding: 0.75rem 1.25rem;
  margin-bottom: -1px;
  background-color: #fff;
  border: 1px solid rgba(0, 0, 0, 0.125);
}

.list-group.draggable-area {
  border: 1px dashed #ccc;
  min-height: 45px;
}
</style>
